// <release-calendar>: release calendar for sections/game-calendar.liquid.
// A 7-day date strip is the source of truth: picking a date selects that day's
// games in the carousel (switching to the nearest release when the day is
// empty). Swiping only moves between games of the displayed day. "Celý měsíc"
// opens a month grid that selects a date the same way.
// Dates are handled as local calendar days ("YYYY-MM-DD"); day differences use
// UTC day numbers so they are never off around midnight or DST changes.

const DAY_MS = 86400000;

const dayNum = (key) => {
  const [y, m, d] = key.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / DAY_MS);
};

const keyFromNum = (num) => new Date(num * DAY_MS).toISOString().slice(0, 10);

const localTodayKey = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const format = (template, values) =>
  (template || '').replace(/\{(\w+)\}/g, (match, name) => (name in values ? values[name] : match));

const escapeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Run `fn` once when the animation finishes — or after its duration anyway,
// since paused/hidden tabs may never fire `finish`.
const afterAnimation = (animation, duration, fn) => {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    fn();
  };
  animation.onfinish = run;
  setTimeout(run, duration + 60);
};

class ReleaseCalendar extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-rc-data]');
    if (!dataEl) return;

    const data = JSON.parse(dataEl.textContent);
    this.t = data.i18n;
    this.lang = this.dataset.lang || document.documentElement.lang || 'cs';
    this.plural = new Intl.PluralRules(this.lang);
    this.dateLong = new Intl.DateTimeFormat(this.lang, { day: 'numeric', month: 'numeric', year: 'numeric', timeZone: 'UTC' });
    this.dateShort = new Intl.DateTimeFormat(this.lang, { day: 'numeric', month: 'numeric', timeZone: 'UTC' });
    this.weekdayShort = new Intl.DateTimeFormat(this.lang, { weekday: 'short', timeZone: 'UTC' });
    this.monthShort = new Intl.DateTimeFormat(this.lang, { month: 'short', timeZone: 'UTC' });

    this.prepareGames(data.games);
    this.platformIcons = {};
    this.querySelectorAll('[data-rc-platform-icons]').forEach((template) =>
      template.content.querySelectorAll('[data-key]').forEach((el) => (this.platformIcons[el.dataset.key] = el.innerHTML))
    );

    this.els = {
      stripLabel: this.querySelector('[data-rc-strip-label]'),
      days: this.querySelector('[data-rc-days]'),
      month: this.querySelector('[data-rc-month]'),
      monthLabel: this.querySelector('[data-rc-month-label]'),
      monthGrid: this.querySelector('[data-rc-month-grid]'),
      weekdays: this.querySelector('[data-rc-weekdays]'),
      monthToggles: this.querySelectorAll('[data-rc-month-toggle], [data-rc-month-picker]'),
      todayBtn: this.querySelector('[data-rc-today]'),
      viewToggle: this.querySelector('[data-rc-view-toggle]'),
      daysView: this.querySelector('[data-rc-days-view]'),
      months: this.querySelector('[data-rc-months]'),
      monthSteps: this.querySelectorAll('[data-rc-month-step]'),
      head: this.querySelector('.rc-head'),
      title: this.querySelector('[data-rc-title]'),
      sub: this.querySelector('[data-rc-sub]'),
      stage: this.querySelector('[data-rc-stage]'),
      track: this.querySelector('[data-rc-track]'),
      live: this.querySelector('[data-rc-live]'),
      info: this.querySelector('[data-rc-info]'),
      listWrap: this.querySelector('[data-rc-list-wrap]'),
      listTitle: this.querySelector('[data-rc-list-title]'),
      list: this.querySelector('[data-rc-list]'),
    };

    this.todayKey = localTodayKey();
    this.renderWeekdays();
    this.bindEvents();

    // Always open on today; drop a #datum=… left over from older links.
    if (/datum=/.test(window.location.hash)) history.replaceState(null, '', window.location.pathname + window.location.search);
    this.select(this.todayKey, { recenter: true, intro: true });
    this.scheduleMidnight();
  }

  // Sort by date, then featured first, then priority, then title (stable).
  prepareGames(games) {
    this.games = games
      .filter((game) => /^\d{4}-\d{2}-\d{2}$/.test(game.date || ''))
      .map((game) => ({ ...game, cover: typeof game.cover === 'string' ? game.cover : '', igdb: typeof game.igdb === 'string' ? game.igdb : '', platforms: Array.isArray(game.platforms) ? game.platforms : [], rating: Math.round(parseFloat(game.rating)) || 0 }))
      .sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? -1 : 1;
        if (a.featured !== b.featured) return a.featured ? -1 : 1;
        const pa = a.priority ?? Infinity;
        const pb = b.priority ?? Infinity;
        if (pa !== pb) return pa - pb;
        return a.title.localeCompare(b.title, this.lang) || String(a.id).localeCompare(String(b.id));
      });

    this.byDate = new Map();
    this.games.forEach((game, index) => {
      game.order = index;
      if (!this.byDate.has(game.date)) this.byDate.set(game.date, []);
      this.byDate.get(game.date).push(game);
    });
    this.dates = [...this.byDate.keys()];
    this.monthCounts = new Map();
    this.games.forEach((game) => {
      const month = game.date.slice(0, 7);
      this.monthCounts.set(month, (this.monthCounts.get(month) || 0) + 1);
    });
  }

  bindEvents() {
    this.els.days.addEventListener('click', (event) => {
      const button = event.target.closest('[data-date]');
      if (button) this.select(button.dataset.date);
    });

    this.addSwipe(this.els.days.parentElement, (direction) => this.stepWeek(direction));

    // "Celý měsíc" opens the day grid; the month name opens the month picker.
    this.els.monthToggles.forEach((button) =>
      button.addEventListener('click', () => {
        const mode = button.hasAttribute('data-rc-month-picker') ? 'months' : 'days';
        if (this.monthOpen && this.monthMode === mode) this.closeMonth(button);
        else if (this.monthOpen) this.setMonthMode(mode);
        else this.openMonth(button, mode);
      })
    );
    this.els.viewToggle.addEventListener('click', () => this.setMonthMode(this.monthMode === 'months' ? 'days' : 'months'));
    this.els.months.addEventListener('click', (event) => {
      const button = event.target.closest('[data-month]');
      if (!button) return;
      const [y, m] = button.dataset.month.split('-').map(Number);
      this.monthView = { y, m };
      this.setMonthMode('days');
    });
    this.els.todayBtn.addEventListener('click', () => this.select(this.todayKey, { recenter: true }));
    this.querySelector('[data-rc-month-close]').addEventListener('click', () => this.closeMonth());
    this.querySelectorAll('[data-rc-month-step]').forEach((button) =>
      button.addEventListener('click', () => this.stepMonth(Number(button.dataset.rcMonthStep)))
    );
    // "Dnes" in the month view: jump to the current month and select today (view stays open).
    this.querySelector('[data-rc-month-today]').addEventListener('click', () => {
      const [y, m] = this.todayKey.split('-').map(Number);
      this.monthView = { y, m };
      this.setMonthMode('days');
      this.select(this.todayKey, { recenter: true });
      this.els.monthGrid.querySelector('[data-today]')?.focus({ preventScroll: true });
    });
    // Picking a day keeps the month view open; it closes via X / Esc / toggle.
    this.els.monthGrid.addEventListener('click', (event) => {
      const button = event.target.closest('[data-date]');
      if (!button) return;
      const key = button.dataset.date;
      const [y, m] = key.split('-').map(Number);
      this.monthView = { y, m }; // a day from the next/previous month switches the view
      this.select(key, { recenter: true });
      this.els.monthGrid.querySelector(`[data-date="${key}"]`)?.focus({ preventScroll: true });
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.monthOpen) this.closeMonth();
    });

    this.querySelectorAll('[data-rc-step]').forEach((button) =>
      button.addEventListener('click', () => this.stepAcrossDays(Number(button.dataset.rcStep)))
    );
    this.els.stage.addEventListener('keydown', (event) => {
      if (event.target.closest('a')) return;
      if (event.key === 'ArrowRight') this.stepAcrossDays(1);
      if (event.key === 'ArrowLeft') this.stepAcrossDays(-1);
    });
    this.els.track.addEventListener('click', (event) => {
      if (this.suppressClick) return;
      const card = event.target.closest('[data-order]');
      if (!card || card.classList.contains('is-active')) return;
      this.goToGame(this.games[Number(card.dataset.order)]);
    });
    this.addCardSwipe();

    this.els.info.addEventListener('click', (event) => {
      const dot = event.target.closest('[data-rc-dot]');
      if (dot) this.setGameIndex(Number(dot.dataset.rcDot));
      const more = event.target.closest('[data-rc-more]');
      if (more) {
        const expanded = more.getAttribute('aria-expanded') !== 'true';
        more.setAttribute('aria-expanded', expanded);
        more.textContent = expanded ? this.t.read_less : this.t.read_more;
        this.els.info.querySelector('.rc-info__summary')?.classList.toggle('is-expanded', expanded);
      }
    });

    this.els.list.addEventListener('click', (event) => {
      if (!event.target.closest('[data-rc-pick]')) return;
      const row = event.target.closest('[data-order]');
      this.goToGame(this.games[Number(row.dataset.order)]);
      this.els.stage.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'center' });
    });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.refreshToday();
    });
  }

  // Horizontal swipe with a live drag on the active card (stage only).
  // Vertical gestures are left to the page (touch-action: pan-y in CSS).
  // Horizontal swipe on the date strip (week back / forward). Vertical
  // gestures are left to the page (touch-action: pan-y in CSS).
  addSwipe(element, onSwipe) {
    let start = null;

    element.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      start = { x: event.clientX, y: event.clientY, dragging: false };
    });

    element.addEventListener('pointermove', (event) => {
      if (!start || start.dragging) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) start = null;
      else if (Math.abs(dx) >= 8) start.dragging = true;
    });

    element.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      const wasDragging = start.dragging;
      start = null;
      if (wasDragging && Math.abs(dx) > 50) onSwipe(dx < 0 ? 1 : -1);
    });
    element.addEventListener('pointercancel', () => (start = null));
  }

  // Carousel drag: the front card follows the finger (with a slight tilt).
  // Release past a quarter of its width — or flick — and it flies out while
  // the next/previous release slides in; otherwise it springs back. At the
  // very first/last release the drag meets resistance. Works across days,
  // like the arrows.
  addCardSwipe() {
    const stage = this.els.stage;
    let drag = null;
    const neighbour = (dir) => {
      const current = (this.byDate.get(this.displayKey) || [])[this.gameIndex];
      return current && this.games[current.order + dir];
    };

    stage.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('.rc-stage__nav')) return;
      const now = performance.now();
      drag = { id: event.pointerId, x0: event.clientX, y0: event.clientY, lastX: event.clientX, lastT: now, v: 0, x: 0, active: false };
      this.suppressClick = false;
    });

    stage.addEventListener('pointermove', (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x0;
      const dy = event.clientY - drag.y0;
      if (!drag.active) {
        if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) {
          drag = null;
          return;
        }
        if (Math.abs(dx) < 8) return;
        if (this.finishFallbackFx()) {
          drag = null;
          return;
        }
        drag.active = true;
        drag.card = this.els.track.querySelector('.rc-card.is-active');
        drag.card?.getAnimations().forEach((animation) => animation.cancel());
        stage.setPointerCapture?.(event.pointerId);
      }
      const now = performance.now();
      drag.v = (event.clientX - drag.lastX) / Math.max(1, now - drag.lastT);
      drag.lastX = event.clientX;
      drag.lastT = now;
      drag.x = dx * (neighbour(dx < 0 ? 1 : -1) ? 1 : 0.3);
      if (drag.card) {
        drag.card.style.translate = `${drag.x}px 0`;
        drag.card.style.rotate = `${drag.x / 30}deg`;
      }
    });

    const end = () => {
      if (!drag) return;
      const { card, x, v, active } = drag;
      drag = null;
      if (!active || !card) return;
      this.suppressClick = true;
      setTimeout(() => (this.suppressClick = false), 0);

      const dir = x < 0 ? 1 : -1;
      const width = card.offsetWidth || 200;
      const flick = Math.abs(v) > 0.5 && Math.sign(v) === -dir;
      card.style.translate = '';
      card.style.rotate = '';
      if ((Math.abs(x) > width * 0.25 || flick) && neighbour(dir)) {
        const duration = reducedMotion() ? 0 : 200;
        const flyOut = card.animate(
          [
            { translate: `${x}px 0`, rotate: `${x / 30}deg`, opacity: 1 },
            { translate: `${-dir * width * 1.2}px 0`, rotate: `${-dir * 12}deg`, opacity: 0 },
          ],
          { duration, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }
        );
        afterAnimation(flyOut, duration, () => {
          this.swipeEnter = true;
          this.stepAcrossDays(dir);
          this.swipeEnter = false;
        });
      } else {
        card.animate(
          [{ translate: `${x}px 0`, rotate: `${x / 30}deg` }, { translate: '0 0', rotate: '0deg' }],
          { duration: reducedMotion() ? 0 : 280, easing: 'cubic-bezier(0.2, 0.8, 0.3, 1.15)' }
        );
      }
    };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
  }


  // ---- State ---------------------------------------------------------------

  select(key, { recenter = false, gameIndex = 0, direction = 0, intro = false } = {}) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(key)) key = this.todayKey;
    if (key === this.selectedKey && !recenter && gameIndex === 0 && this.displayKey === key) return;

    this.stopFallbackFx();
    this.fallbackFrom = null;

    // Empty day: switch to the nearest release (next one, else the last one)
    // and say so in the headline. Animated unless reduced motion is on.
    if (!this.byDate.has(key)) {
      const next = this.dates.find((date) => date > key);
      const target = next || [...this.dates].reverse().find((date) => date < key);
      if (target) {
        this.fallbackFrom = { key, kind: next ? 'after' : 'before' };
        if (!reducedMotion()) {
          this.playFallbackFx(key, target, intro);
          return;
        }
        key = target;
      }
    }

    this.applySelection(key, { recenter, gameIndex, direction });
  }

  applySelection(key, { recenter = false, gameIndex = 0, direction = 0 } = {}) {
    this.selectedKey = key;
    this.displayKey = this.byDate.has(key) ? key : null;
    this.mode = 'exact';
    const dayGames = this.byDate.get(this.displayKey) || [];
    this.gameIndex = Math.min(Math.max(gameIndex, 0), Math.max(dayGames.length - 1, 0));
    this.keepInStrip(key, recenter);
    this.render(direction);
  }

  keepInStrip(key, recenter) {
    const num = dayNum(key);
    if (recenter || this.stripStart === undefined || num < this.stripStart || num > this.stripStart + 6) {
      this.stripStart = num - 2;
    }
  }

  // Empty day effect. While "searching", the empty day stays selected and the
  // carousel shows the previous release; the deck then shuffles and deals out
  // the nearest release, and the selection moves to its date. On page load it
  // waits a moment first so the visitor can get their bearings.
  playFallbackFx(emptyKey, target, intro) {
    const token = ++this.fxToken;
    const { t } = this;
    const later = (ms, fn) => this.fxTimers.push(setTimeout(() => token === this.fxToken && fn(), ms));
    this.fxTarget = target;
    const start = intro ? 1500 : 120;

    this.selectedKey = emptyKey;
    this.keepInStrip(emptyKey, intro);
    this.renderStrip();

    // Previous release as the "before" picture (falls back to the target).
    const before = [...this.dates].reverse().find((date) => date < emptyKey) || target;
    this.displayKey = before;
    this.mode = 'exact';
    this.gameIndex = before === target ? 0 : this.byDate.get(before).length - 1;
    this.renderStage(0);
    this.renderInfo();
    this.renderList();

    this.els.title.textContent = format(t.empty_day, { date: this.longDate(emptyKey) });
    this.els.sub.textContent = this.relative(emptyKey);
    this.els.track.classList.add('is-pending');
    this.els.info.classList.add('is-dimmed');

    // Cards that end up on screen: the target and its neighbours. Random
    // releases from around the empty day pass through the deck meanwhile.
    const targetGame = this.byDate.get(target)[0];
    const finals = { prev: this.games[targetGame.order - 1], active: targetGame, next: this.games[targetGame.order + 1] };
    const pivot = this.byDate.get(before).slice(-1)[0];
    const lo = Math.max(0, Math.min(pivot.order, targetGame.order) - 8);
    const pool = this.games
      .slice(lo, Math.max(pivot.order, targetGame.order) + 9)
      .filter((game) => !Object.values(finals).includes(game));
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    [...pool.slice(-6), ...Object.values(finals)].forEach(
      (game) => game?.cover && (new Image().src = this.coverUrl(game, 't_cover_big'))
    );
    if (targetGame.cover) new Image().src = this.coverUrl(targetGame, 't_cover_big_2x');

    // "Hledám…" appears together with the shuffle.
    later(start, () => {
      this.els.sub.innerHTML = `<span class="rc-searching">${escapeHtml(t.searching)}</span>`;
      this.els.track.classList.remove('is-pending');
      this.shuffleDeck(pool, finals, later, () => {
        this.els.info.classList.remove('is-dimmed');
        this.applySelection(target);
        this.els.head.animate(
          [{ opacity: 0, filter: 'blur(0.3rem)' }, { opacity: 1, filter: 'blur(0)' }],
          { duration: 400, easing: 'ease-out' }
        );
        this.fxTimers = [];
        // Final touch: a soft glow + light sweep once the deck has faded out.
        setTimeout(() => {
          const cover = token === this.fxToken && this.els.track.querySelector('.rc-card.is-active .rc-cover');
          if (!cover) return;
          cover.classList.add('is-landed');
          setTimeout(() => cover.classList.remove('is-landed'), 1200);
        }, 200);
      });
    });
  }

  // Card-table shuffle on an overlay deck: the three visible cards keep
  // trading places (left → centre → right → behind → left). Whichever card
  // passes behind picks up another game's cover; the last passes bring the
  // target and its neighbours, so the deck settles exactly into the real
  // carousel, which then fades in underneath.
  shuffleDeck(pool, finals, later, done) {
    const { stage, track } = this.els;
    const stageRect = stage.getBoundingClientRect();
    const boxes = ['prev', 'active', 'next'].map((role) => {
      const card = track.querySelector(`.rc-card--${role}`);
      return card && !card.classList.contains('is-empty') ? (card.querySelector('.rc-cover') || card).getBoundingClientRect() : null;
    });
    const center = boxes[1];
    if (!center) return done();
    const cx = center.left + center.width / 2;
    // Mirror a missing side from the other one.
    const mirror = (box) => ({ left: 2 * cx - (box.left + box.width), width: box.width, top: box.top, height: box.height });
    if (!boxes[0] && boxes[2]) boxes[0] = mirror(boxes[2]);
    if (!boxes[2] && boxes[0]) boxes[2] = mirror(boxes[0]);
    if (!boxes[0]) return done();
    const cy = center.top + center.height / 2;

    const slot = (box, dim) => ({
      transform: `translate(${box.left + box.width / 2 - cx}px, ${box.top + box.height / 2 - cy}px) scale(${box.width / center.width})`,
      filter: `brightness(${dim ? 0.55 : 1})`,
      opacity: dim ? 0.55 : 1,
    });
    const slots = [slot(boxes[0], true), slot(boxes[1], false), slot(boxes[2], true)];
    const behind = { transform: `translate(0px, ${center.height * 0.05}px) scale(0.6)`, filter: 'brightness(0.4)', opacity: 0.3 };

    const deck = document.createElement('div');
    deck.className = 'rc-deck';
    deck.setAttribute('aria-hidden', 'true');
    const startGames = ['prev', 'active', 'next'].map((role) => {
      const el = track.querySelector(`.rc-card--${role}`);
      return el?.dataset.order ? this.games[Number(el.dataset.order)] : null;
    });
    const cards = startGames.map((game, i) => {
      const card = document.createElement('div');
      card.className = 'rc-deck__card rc-cover';
      Object.assign(card.style, {
        left: `${center.left - stageRect.left}px`,
        top: `${center.top - stageRect.top}px`,
        width: `${center.width}px`,
        height: `${center.height}px`,
        transform: slots[i].transform,
        filter: slots[i].filter,
        opacity: game ? slots[i].opacity : 0,
        zIndex: i === 1 ? 3 : 1,
      });
      card.innerHTML = game ? this.coverHtml(game, 't_cover_big', true) : '';
      card.dataset.empty = game ? '' : '1';
      card.deckSlot = i;
      deck.appendChild(card);
      return card;
    });
    stage.appendChild(deck);
    track.classList.add('is-shuffling');

    // Six rotations: steady, then slowing into the result.
    const steps = [320, 300, 300, 320, 400, 560];
    const n = steps.length;
    const coverFor = (k) => (k === n - 2 ? finals.next : k === n - 1 ? finals.active : k === n ? finals.prev : pool.pop());
    let at = 0;
    steps.forEach((duration, i) => {
      const k = i + 1;
      const easing = k === n ? 'cubic-bezier(0.25, 0.8, 0.25, 1)' : 'cubic-bezier(0.45, 0, 0.25, 1)';
      later(at, () => {
        cards.forEach((card) => {
          const from = { ...slots[card.deckSlot], opacity: card.dataset.empty ? 0 : slots[card.deckSlot].opacity };
          const to = (card.deckSlot + 1) % 3;
          let frames;
          if (card.deckSlot === 2) {
            // Right → behind the deck → left; swap the cover while hidden.
            card.style.zIndex = 1;
            const game = coverFor(k);
            setTimeout(() => {
              card.innerHTML = game ? this.coverHtml(game, 't_cover_big', true) : '';
              card.dataset.empty = game ? '' : '1';
            }, duration * 0.45);
            frames = [from, { ...behind, offset: 0.5 }, { ...slots[to], opacity: game ? slots[to].opacity : 0 }];
          } else {
            // Left → centre on top, centre → right just below it.
            card.style.zIndex = card.deckSlot === 0 ? 3 : 2;
            frames = [from, { ...slots[to], opacity: card.dataset.empty ? 0 : slots[to].opacity }];
          }
          card.animate(frames, { duration, easing, fill: 'forwards' });
          card.deckSlot = to;
        });
      });
      at += duration;
    });

    // Settle: render the real carousel underneath and fade the deck away.
    later(at, () => {
      track.classList.remove('is-shuffling');
      done();
      afterAnimation(deck.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease-out', fill: 'forwards' }), 240, () => deck.remove());
    });
  }

  // Interaction during the effect (arrows, swipe, dots): jump to the result.
  finishFallbackFx() {
    if (!this.fxTimers?.length) return false;
    const target = this.fxTarget;
    this.stopFallbackFx();
    this.applySelection(target);
    return true;
  }

  stopFallbackFx() {
    this.fxToken = (this.fxToken || 0) + 1;
    (this.fxTimers || []).forEach(clearTimeout);
    this.fxTimers = [];
    if (!this.els) return;
    this.els.stage.querySelector('.rc-deck')?.remove();
    this.els.track.classList.remove('is-pending', 'is-shuffling');
    this.els.info.classList.remove('is-dimmed');
  }



  goToGame(game) {
    if (!game) return;
    const index = this.byDate.get(game.date).indexOf(game);
    if (game.date === this.displayKey && this.mode === 'exact') {
      this.setGameIndex(index);
    } else {
      const current = (this.byDate.get(this.displayKey) || [])[this.gameIndex];
      const direction = current ? Math.sign(game.order - current.order) : 0;
      this.select(game.date, { gameIndex: index, direction });
    }
  }

  // Arrow buttons / keys: next game in the day, then continue to the neighbouring
  // release (switching the date). The carousel drag uses it too.
  stepAcrossDays(direction) {
    if (this.finishFallbackFx()) return;
    const dayGames = this.byDate.get(this.displayKey) || [];
    const next = this.gameIndex + direction;
    if (next >= 0 && next < dayGames.length) {
      this.setGameIndex(next);
      return;
    }
    const current = dayGames[this.gameIndex];
    const neighbour = current && this.games[current.order + direction];
    if (neighbour) this.goToGame(neighbour);
    else this.bounce(direction);
  }

  setGameIndex(index) {
    if (this.finishFallbackFx()) return;
    if (index === this.gameIndex) return;
    const direction = index > this.gameIndex ? 1 : -1;
    this.gameIndex = index;
    this.renderStage(direction);
    this.renderInfo();
    this.renderList();
    const count = (this.byDate.get(this.displayKey) || []).length;
    this.els.live.textContent = format(this.t.game_of, { index: index + 1, count });
  }

  // Swiping the strip moves the selection by 7 days (same weekday) and the strip with it.
  stepWeek(direction) {
    this.stripStart += direction * 7;
    this.select(keyFromNum(dayNum(this.selectedKey) + direction * 7));
  }

  refreshToday() {
    const today = localTodayKey();
    if (today === this.todayKey) return;
    this.todayKey = today;
    this.render(0);
    if (this.monthOpen) this.renderMonth();
  }

  scheduleMidnight() {
    const now = new Date();
    const midnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 5);
    clearTimeout(this.midnightTimer);
    this.midnightTimer = setTimeout(() => {
      this.refreshToday();
      this.scheduleMidnight();
    }, midnight - now);
  }

  // ---- Copy helpers ----------------------------------------------------------

  pluralKey(base, count) {
    return this.t[`${base}_${this.plural.select(count) === 'few' ? 'few' : 'other'}`];
  }

  longDate(key) {
    return this.dateLong.format(new Date(dayNum(key) * DAY_MS)).replace(/ /g, ' ');
  }

  shortDate(key) {
    return this.dateShort.format(new Date(dayNum(key) * DAY_MS)).replace(/ /g, ' ');
  }

  relative(key) {
    const diff = dayNum(key) - dayNum(this.todayKey);
    if (diff === 0) return this.t.rel_today;
    if (diff === 1) return this.t.rel_tomorrow;
    if (diff === -1) return this.t.rel_yesterday;
    if (diff > 0) return format(this.pluralKey('rel_future', diff), { count: diff });
    return format(this.pluralKey('rel_past', -diff), { count: -diff });
  }

  dayLabel(key, count) {
    const date = this.longDate(key);
    if (count === 0) return format(this.t.day_none, { date });
    if (count === 1) return format(this.t.day_one, { date });
    return format(this.pluralKey('day', count), { date, count });
  }

  monthName(y, m) {
    return `${this.t.month_names[m - 1] || m} ${y}`;
  }

  // ---- Rendering -------------------------------------------------------------

  render(direction) {
    if (this.monthOpen && this.monthMode === 'days') this.renderMonth();
    this.renderStrip();
    this.renderHeadline();
    this.renderStage(direction);
    this.renderInfo();
    this.renderList();
  }

  renderStrip() {
    const [y, m] = this.selectedKey.split('-').map(Number);
    this.els.stripLabel.textContent = this.monthName(y, m);
    this.els.todayBtn.disabled = this.selectedKey === this.todayKey;

    let html = '';
    for (let i = 0; i < 7; i++) {
      const key = keyFromNum(this.stripStart + i);
      html += this.dayButton(key, 'rc-day');
    }
    this.els.days.innerHTML = html;
  }

  dayButton(key, className, outside = false) {
    const games = this.byDate.get(key) || [];
    const count = games.length;
    const featured = games.some((game) => game.featured);
    const selected = key === this.selectedKey;
    const today = key === this.todayKey;
    const weekday = this.weekdayShort.format(new Date(dayNum(key) * DAY_MS)).replace('.', '');
    // One dot: red = something releases, gold = a big featured release that day.
    const dots = count === 0 ? '' : `<i class="rc-dot${featured ? ' rc-dot--featured' : ''}"></i>`;
    const label = this.dayLabel(key, count) + (featured ? `, ${this.t.featured}` : '');
    return `<button type="button" class="${className}${selected ? ' is-selected' : ''}${today ? ' is-today' : ''}${outside ? ' is-outside' : ''}"
      data-date="${key}" aria-pressed="${selected}" aria-label="${escapeHtml(label)}"${today ? ' data-today' : ''}>
      <span class="${className}__num">${Number(key.slice(8))}</span>
      ${className === 'rc-day' ? `<span class="rc-day__wd">${escapeHtml(weekday)}</span>` : ''}
      <span class="rc-dots" aria-hidden="true">${dots}</span>
    </button>`;
  }

  renderHeadline() {
    const { t } = this;
    const key = this.displayKey;
    const games = this.byDate.get(key) || [];
    if (!key) {
      this.els.title.textContent = format(t.empty_day, { date: this.longDate(this.selectedKey) });
      this.els.sub.textContent = '';
      return;
    }

    const diff = dayNum(key) - dayNum(this.todayKey);
    const date = this.longDate(key);
    const count = games.length;
    let sub;
    if (count > 1) {
      this.els.title.textContent = format(this.pluralKey(diff >= 0 ? 'multi_future' : 'multi_past', count), { count, date });
      sub = diff === 0 ? `${t.rel_today} · ${t.tagline}` : this.relative(key);
    } else {
      this.els.title.textContent = format(diff === 0 ? t.today_single : diff > 0 ? t.future_single : t.past_single, { date });
      sub = diff === 0 ? t.tagline : this.relative(key);
    }

    // Reached via an empty day: keep explaining why the date moved.
    if (this.fallbackFrom) {
      const from = this.fallbackFrom.key;
      const fromDate = from.slice(0, 4) === key.slice(0, 4) ? this.shortDate(from) : this.longDate(from);
      const rel = this.relative(key);
      const note = format(this.fallbackFrom.kind === 'after' ? t.found_after : t.found_before, {
        date: fromDate,
        rel: rel.charAt(0).toLocaleLowerCase(this.lang) + rel.slice(1),
      });
      this.els.sub.innerHTML = `<span class="rc-note"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="8" r="1.1" fill="currentColor"/></svg>${escapeHtml(note)}</span>`;
      return;
    }
    this.els.sub.textContent = sub;
  }

  // Purchase buttons. Released (incl. today): "Koupit" only for products in
  // stock. Upcoming: "Předobjednat" when the game is imported as a product
  // (pre-order products first). Otherwise no purchase button.
  purchaseLinks(game) {
    const products = game.products || [];
    if (game.date > this.todayKey) {
      const preorders = products.filter((product) => product.preorder);
      return { label: this.t.preorder, items: preorders.length ? preorders : products };
    }
    return { label: this.t.buy, items: products.filter((product) => product.available) };
  }

  // "83 %" pill; green from 75, neutral below. Hidden when there is no rating.
  ratingHtml(game, className) {
    if (!(game.rating > 0)) return '';
    const label = format(this.t.rating, { value: game.rating }).replace(/ %/, '\u00a0%');
    const value = this.lang.startsWith('cs') || this.lang.startsWith('sk') ? `${game.rating}\u00a0%` : `${game.rating}%`;
    return `<span class="${className}${game.rating >= 75 ? ' is-high' : ''}" title="${escapeHtml(label)}"><span class="visually-hidden">${escapeHtml(label)}</span><span class="rc-rating__src" aria-hidden="true">IGDB</span><span aria-hidden="true">${value}</span></span>`;
  }

  // Platform chips with brand marks (monochrome) + short name.
  platformsHtml(game, className) {
    const items = this.platformList(game.platforms);
    if (!items.length) return '';
    return `<ul class="${className}" role="list" aria-label="${escapeHtml(this.t.platforms_label)}">${items
      .map(({ key, label }) => `<li class="rc-platform">${key && this.platformIcons[key] ? this.platformIcons[key] : ''}<span>${escapeHtml(label)}</span></li>`)
      .join('')}</ul>`;
  }

  coverUrl(game, size) {
    if (!game.cover) return '';
    return game.cover.replace(/\/t_[a-z0-9_]+\//, `/${size}/`);
  }

  coverHtml(game, size, eager) {
    const fallback = `<span class="rc-cover__fallback"><span>${escapeHtml(game.title)}</span></span>`;
    if (!game.cover) return fallback;
    return `<img src="${escapeHtml(this.coverUrl(game, size))}" alt="" width="264" height="352"
      loading="${eager ? 'eager' : 'lazy'}" onerror="this.replaceWith(this.nextElementSibling.content.cloneNode(true))">
      <template>${fallback}</template>`;
  }

  renderStage(direction) {
    const dayGames = this.byDate.get(this.displayKey) || [];
    const current = dayGames[this.gameIndex];
    if (!current) {
      this.els.track.innerHTML = '';
      return;
    }

    const prev = this.games[current.order - 1];
    const next = this.games[current.order + 1];
    const card = (game, role) => {
      if (!game) return `<div class="rc-card rc-card--${role} is-empty" aria-hidden="true"></div>`;
      const active = role === 'active';
      const counter = active && dayGames.length > 1 ? `<span class="rc-card__counter">${this.gameIndex + 1} / ${dayGames.length}</span>` : '';
      const badge = active && game.featured ? `<span class="rc-card__badge"><svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.2l2.6 5.27 5.82.85-4.21 4.1.99 5.79L12 16.98l-5.2 2.73.99-5.79-4.21-4.1 5.82-.85z" fill="currentColor"/></svg>${escapeHtml(this.t.featured)}</span>` : '';
      return `<div class="rc-card rc-card--${role}${active ? ' is-active' : ''}" data-order="${game.order}"
        ${active ? '' : `aria-hidden="true" title="${escapeHtml(game.title)}"`}>
        ${badge}
        <div class="rc-cover">${this.coverHtml(game, active ? 't_cover_big_2x' : 't_cover_big', active)}${counter}</div>
      </div>`;
    };

    this.els.track.innerHTML = card(prev, 'prev') + card(current, 'active') + card(next, 'next');
    // Arrows continue across days; hide one only at the very first/last release.
    this.querySelector('[data-rc-step="-1"]').hidden = !prev;
    this.querySelector('[data-rc-step="1"]').hidden = !next;

    if (direction && !reducedMotion()) {
      const frames = this.swipeEnter
        ? [{ opacity: 0, translate: `${direction * 55}% 0`, rotate: `${direction * 8}deg` }, { opacity: 1, translate: '0 0', rotate: '0deg' }]
        : [{ opacity: 0.4, translate: `${direction * 40}px 0` }, { opacity: 1, translate: '0 0' }];
      this.els.track.querySelector('.is-active')?.animate(frames, {
        duration: this.swipeEnter ? 320 : 220,
        easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)',
      });
    }
  }

  bounce(direction) {
    if (reducedMotion()) return;
    this.els.track.querySelector('.is-active')?.animate(
      [{ translate: '0 0' }, { translate: `${direction * -12}px 0` }, { translate: '0 0' }],
      { duration: 220, easing: 'ease-out' }
    );
  }

  renderInfo() {
    const dayGames = this.byDate.get(this.displayKey) || [];
    const game = dayGames[this.gameIndex];
    if (!game) {
      this.els.info.innerHTML = '';
      return;
    }

    const genres = (game.genres || []).filter(Boolean);
    const buyIcon = '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3 4h2l2.4 10.2a1 1 0 0 0 1 .8h8.9a1 1 0 0 0 1-.8L20 7H6.2" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9.5" cy="19" r="1.4" fill="currentColor"/><circle cx="17" cy="19" r="1.4" fill="currentColor"/></svg>';
    const { label: buyLabel, items: products } = this.purchaseLinks(game);
    const buttons = products
      .map((product) => `<a class="rc-btn rc-btn--primary" href="${escapeHtml(product.url)}">${buyIcon}${escapeHtml(buyLabel)}${products.length > 1 && product.label ? ` · ${escapeHtml(product.label)}` : ''}</a>`)
      .join('');
    const dots = dayGames.length > 1
      ? `<div class="rc-dots-nav">${dayGames.map((g, i) => `<button type="button" class="rc-dots-nav__dot${i === this.gameIndex ? ' is-active' : ''}" data-rc-dot="${i}" aria-label="${escapeHtml(format(this.t.game_of, { index: i + 1, count: dayGames.length }))}"${i === this.gameIndex ? ' aria-current="true"' : ''}></button>`).join('')}</div>`
      : '';

    this.els.info.innerHTML = `
      <h3 class="rc-info__title">${escapeHtml(game.title)}</h3>
      ${genres.length ? `<p class="rc-info__genres">${genres.map(escapeHtml).join(' · ')}</p>` : ''}
      ${game.rating > 0 || game.platforms.length ? `<div class="rc-info__meta">${this.ratingHtml(game, 'rc-rating')}${this.platformsHtml(game, 'rc-platforms')}</div>` : ''}
      ${game.summary ? `<p class="rc-info__summary" id="RcSummary">${escapeHtml(game.summary)}</p>
        ${game.summary.length > 200 ? `<button type="button" class="rc-info__more" data-rc-more aria-expanded="false" aria-controls="RcSummary">${escapeHtml(this.t.read_more)}</button>` : ''}` : ''}
      ${buttons ? `<div class="rc-info__actions${products.length === 1 ? ' is-single' : ''}">${buttons}</div>` : ''}
      ${dots}`;
  }

  // Month list under the carousel: every release in the displayed month, with
  // the details of the original calendar cards.
  renderList() {
    const key = this.displayKey || this.selectedKey;
    const month = key.slice(0, 7);
    const [y, m] = month.split('-').map(Number);
    const items = this.games.filter((game) => game.date.startsWith(month));

    this.els.listWrap.hidden = items.length === 0;
    // "Herní kalendář na říjen 2026" — month lower-cased except in English.
    const monthLabel = this.monthName(y, m);
    this.els.listTitle.textContent = format(this.t.list_title, {
      month: this.lang.startsWith('en') ? monthLabel : monthLabel.toLocaleLowerCase(this.lang),
    });

    const dayGames = this.byDate.get(this.displayKey) || [];
    const current = dayGames[this.gameIndex];
    this.els.list.innerHTML = items
      .map((game) => this.monthCard(game, game === current))
      .join('');
  }

  monthCard(game, isCurrent) {
    const { t } = this;
    const date = new Date(dayNum(game.date) * DAY_MS);
    const isReleased = game.date < this.todayKey;
    const genres = (game.genres || []).filter(Boolean);
    const { label: buyLabel, items: products } = this.purchaseLinks(game);
    const actions =
      products
        .map((product) => `<a class="rc-mcard__btn rc-mcard__btn--primary" href="${escapeHtml(product.url)}">${escapeHtml(buyLabel)}${products.length > 1 && product.label ? ` · ${escapeHtml(product.label)}` : ''}</a>`)
        .join('') +
      (game.igdb ? `<a class="rc-mcard__btn rc-mcard__btn--secondary" href="${escapeHtml(game.igdb)}" target="_blank" rel="noopener noreferrer">IGDB ↗</a>` : '');

    return `<li>
      <article class="rc-mcard${isCurrent ? ' is-current' : ''}${isReleased ? ' is-released' : ''}" data-order="${game.order}"${isCurrent ? ' aria-current="true"' : ''}>
        <div class="rc-mcard__date">
          <span class="rc-mcard__day">${date.getUTCDate()}</span>
          <span class="rc-mcard__month">${escapeHtml(this.monthShort.format(date).replace('.', ''))}</span>
          <span class="rc-mcard__year">${date.getUTCFullYear()}</span>
          ${isReleased ? `<span class="rc-mcard__released">${escapeHtml(t.released)}</span>` : ''}
        </div>
        <button type="button" class="rc-mcard__cover rc-cover" data-rc-pick tabindex="-1" aria-hidden="true">${this.coverHtml(game, 't_cover_big', false)}</button>
        <div class="rc-mcard__body">
          <h3 class="rc-mcard__title"><button type="button" data-rc-pick>${escapeHtml(game.title)}</button></h3>
          ${game.platforms.length || game.rating > 0 ? `<div class="rc-mcard__meta">${this.ratingHtml(game, 'rc-rating rc-rating--small')}${this.platformsHtml(game, 'rc-platforms rc-platforms--small')}</div>` : ''}
          ${genres.length ? `<p class="rc-mcard__genres">${genres.map(escapeHtml).join(' · ')}</p>` : ''}
          ${game.summary ? `<p class="rc-mcard__summary">${escapeHtml(game.summary)}</p>` : ''}
        </div>
        ${actions ? `<div class="rc-mcard__actions">${actions}</div>` : ''}
      </article>
    </li>`;
  }

  // Short platform names + brand-mark key (icons stay monochrome).
  platformList(platforms) {
    const seen = new Set();
    return (platforms || [])
      .map((raw) => {
        const name = String(raw).trim();
        const low = name.toLowerCase();
        if (low === 'pc' || low.includes('windows')) return { key: 'pc', label: 'PC' };
        if (low.includes('playstation 5') || low === 'ps5') return { key: 'playstation', label: 'PS5' };
        if (low.includes('playstation 4') || low === 'ps4') return { key: 'playstation', label: 'PS4' };
        if (low.includes('xbox series')) return { key: 'xbox', label: 'Xbox Series' };
        if (low.includes('xbox one')) return { key: 'xbox', label: 'Xbox One' };
        if (low.includes('switch 2')) return { key: 'nintendo', label: 'Switch 2' };
        if (low.includes('nintendo switch')) return { key: 'nintendo', label: 'Switch' };
        return { key: '', label: name };
      })
      .filter(({ label }) => label && !seen.has(label) && seen.add(label));
  }

  // ---- Month overlay -----------------------------------------------------

  // 12-month overview for one year with release counts.
  renderMonthPicker(y) {
    const pad = (n) => String(n).padStart(2, '0');
    const selectedMonth = this.selectedKey.slice(0, 7);
    const todayMonth = this.todayKey.slice(0, 7);
    let html = '';
    for (let m = 1; m <= 12; m++) {
      const month = `${y}-${pad(m)}`;
      const count = this.monthCounts.get(month) || 0;
      const countLabel = count === 1 ? this.t.month_count_one : format(this.pluralKey('month_count', count), { count });
      html += `<button type="button" class="rc-mpick${month === selectedMonth ? ' is-selected' : ''}${month === todayMonth ? ' is-today' : ''}${count ? '' : ' is-empty'}"
        data-month="${month}" aria-pressed="${month === selectedMonth}" aria-label="${escapeHtml(`${this.monthName(y, m)}, ${count ? countLabel : format(this.t.day_none, { date: '' }).replace(/^,\s*/, '')}`)}">
        <span class="rc-mpick__name">${escapeHtml(this.t.month_names[m - 1] || m)}</span>
        <span class="rc-mpick__count">${count ? escapeHtml(countLabel) : '–'}</span>
      </button>`;
    }
    this.els.months.innerHTML = html;
  }

  renderWeekdays() {
    // 2024-01-01 was a Monday.
    const monday = dayNum('2024-01-01');
    let html = '';
    for (let i = 0; i < 7; i++) {
      html += `<span>${escapeHtml(this.weekdayShort.format(new Date((monday + i) * DAY_MS)).replace('.', ''))}</span>`;
    }
    this.els.weekdays.innerHTML = html;
  }

  openMonth(trigger, mode = 'days') {
    const [y, m] = this.selectedKey.split('-').map(Number);
    this.monthView = { y, m };
    this.monthOpen = true;
    this.monthTrigger = trigger;
    this.els.month.hidden = false;
    this.classList.add('is-month-open');
    this.setMonthMode(mode);
  }

  setMonthMode(mode) {
    this.monthMode = mode;
    this.els.daysView.hidden = mode !== 'days';
    this.els.months.hidden = mode !== 'months';
    this.els.viewToggle.setAttribute('aria-expanded', mode === 'months');
    this.els.monthToggles.forEach((button) => {
      const own = button.hasAttribute('data-rc-month-picker') ? 'months' : 'days';
      button.setAttribute('aria-expanded', this.monthOpen && own === mode);
    });
    this.classList.toggle('is-picking-month', mode === 'months');
    this.renderMonth();
    const target = mode === 'months'
      ? this.els.months.querySelector('.is-selected') || this.els.months.querySelector('button')
      : this.els.monthGrid.querySelector('.is-selected') || this.els.monthGrid.querySelector('button');
    target?.focus({ preventScroll: true });
  }

  closeMonth(focusTarget) {
    if (!this.monthOpen) return;
    this.monthOpen = false;
    this.els.month.hidden = true;
    this.els.monthToggles.forEach((button) => button.setAttribute('aria-expanded', 'false'));
    this.classList.remove('is-month-open', 'is-picking-month');
    (focusTarget || this.monthTrigger)?.focus({ preventScroll: true });
  }

  stepMonth(direction) {
    let { y, m } = this.monthView;
    if (this.monthMode === 'months') {
      this.monthView = { y: y + direction, m };
      this.renderMonth();
      return;
    }
    m += direction;
    if (m < 1) {
      m = 12;
      y -= 1;
    } else if (m > 12) {
      m = 1;
      y += 1;
    }
    this.monthView = { y, m };
    this.renderMonth();
  }

  renderMonth() {
    const { y, m } = this.monthView;
    const picking = this.monthMode === 'months';
    this.els.monthLabel.textContent = picking ? String(y) : this.monthName(y, m);
    this.els.monthSteps.forEach((button) => {
      const next = button.dataset.rcMonthStep === '1';
      button.setAttribute('aria-label', picking ? (next ? this.t.next_year : this.t.prev_year) : next ? this.t.next_month : this.t.prev_month);
    });
    if (picking) {
      this.renderMonthPicker(y);
      return;
    }

    const pad = (n) => String(n).padStart(2, '0');
    const first = dayNum(`${y}-${pad(m)}-01`);
    const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
    const lead = (new Date(first * DAY_MS).getUTCDay() + 6) % 7; // Monday-based
    const cells = Math.ceil((lead + daysInMonth) / 7) * 7;

    let html = '';
    for (let i = 0; i < cells; i++) {
      const num = first - lead + i;
      const key = keyFromNum(num);
      html += this.dayButton(key, 'rc-mday', i < lead || i >= lead + daysInMonth);
    }
    this.els.monthGrid.innerHTML = html;
  }
}

customElements.define('release-calendar', ReleaseCalendar);
